import React, { useEffect } from 'react';
import SEO from '../components/common/SEO';
import Layout from '../components/layout/Layout';
import SplitHero from '../components/home/SplitHero';
import Features from '../components/home/Features';
import HowItWorksImage from '../components/home/HowItWorksImage';
import Audience from '../components/home/Audience';
import Pricing from '../components/home/Pricing';
import Testimonials from '../components/home/Testimonials';
import LatestPosts from '../components/home/LatestPosts';
import FAQNew, { FAQS } from '../components/home/FAQNew';
import CTA from '../components/home/CTA';
import { siteConfig } from '../config/site';

const HomePage: React.FC = () => {
  // Inject JSON-LD schemas for SEO: Organization + WebSite + FAQPage + Product.
  // Rich results eligible: FAQ snippets, sitelinks searchbox, product info.
  useEffect(() => {
    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      logo: `${siteConfig.domain}/logo512.png`,
      description: siteConfig.brand.description,
      email: siteConfig.contact.orderEmail,
      contactPoint: [{
        '@type': 'ContactPoint',
        contactType: 'customer support',
        telephone: siteConfig.contact.phoneDisplay,
        availableLanguage: ['English'],
      }],
    };
    const website = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteConfig.brand.name,
      url: siteConfig.domain,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteConfig.domain}/blog?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const product = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${siteConfig.brand.name} — Flash USDT Software`,
      description: siteConfig.brand.description,
      brand: { '@type': 'Brand', name: siteConfig.brand.name },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '15',
        highPrice: '5000',
        offerCount: '3',
        offers: [
          { '@type': 'Offer', name: 'Demo License', price: '15', priceCurrency: 'USD', url: `${siteConfig.domain}/payment/demo` },
          { '@type': 'Offer', name: 'Basic License (Yearly)', price: '2500', priceCurrency: 'USD', url: `${siteConfig.domain}/payment/basic` },
          { '@type': 'Offer', name: 'Pro License (Lifetime)', price: '5000', priceCurrency: 'USD', url: `${siteConfig.domain}/payment/pro` },
        ],
      },
    };
    const tags: HTMLScriptElement[] = [];
    for (const [id, obj] of [
      ['ld-organization', org],
      ['ld-website', website],
      ['ld-faq', faq],
      ['ld-product', product],
    ] as const) {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(obj);
      tags.push(script);
    }
    return () => tags.forEach((t) => t.remove());
  }, []);

  return (
    <Layout>
      <SEO
        title={`${siteConfig.brand.name} — Buy Flash USDT Software for TRC-20, ERC-20 & BEP-20`}
        description={`Buy ${siteConfig.brand.name} — licensed flash USDT software for TRC-20, ERC-20 & BEP-20. 50+ compatible wallets, 24/7 WhatsApp support, 365-day flash validity. Three plans from $15.`}
        keywords="flash USDT website, buy flash USDT, flash USDT software, USDT flasher, TRC-20 flash USDT, ERC-20 flash USDT, BEP-20 flash USDT, USDT simulation software, tether flasher, flash USDT license, USDT flashing tool"
      />
      <SplitHero />
      <Features />
      <HowItWorksImage />
      <Audience />
      <Pricing />
      <Testimonials />
      <LatestPosts />
      <FAQNew />
      <CTA />
    </Layout>
  );
};

export default HomePage;
